import React, { useState, useEffect } from 'react';
import doctorSignatureService from '../../services/doctorSignatureService';

/* ─── helpers ─────────────────────────────────────────────── */
const fmt   = d => d ? new Date(d).toLocaleDateString('en-IN',  { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtDT = d => d ? new Date(d).toLocaleString ('en-IN',  { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true }) : '—';

function interpColor(v) {
  if (!v) return { color:'#222' };
  const u = v.toUpperCase();
  if (u==='S')              return { color:'#145a32', fontWeight:800 };
  if (u==='R')              return { color:'#7b241c', fontWeight:800 };
  if (u==='I'||u==='IE')   return { color:'#7d6608', fontWeight:800 };
  return { color:'#1a1a1a', fontWeight:700 };
}

/* ─── shared print-ready styles ──────────────────────────── */
const FONT = "'Arial', 'Helvetica', sans-serif";

const th = (extra={}) => ({
  padding:'5px 8px', textAlign:'left', fontSize:'10px',
  fontWeight:700, color:'#1a1a1a', background:'#e8e8e8',
  border:'1px solid #b0b0b0', whiteSpace:'nowrap', ...extra
});
const td = (extra={}) => ({
  padding:'5px 8px', fontSize:'11px', color:'#1a1a1a',
  border:'1px solid #cccccc', verticalAlign:'top', ...extra
});
const labelCell = {
  ...td(), background:'#f4f4f4', fontWeight:600,
  fontSize:'10px', color:'#444', width:'14%', whiteSpace:'nowrap'
};
const valueCell = { ...td(), fontSize:'11px', fontWeight:500, width:'19%' };

/* ═══════════════════════════════════════════════════════════ */
export default function MicrobiologyReportModal({ data, isOpen, onClose }) {
  const [signatures, setSignatures] = useState([]);

  useEffect(() => {
    if (isOpen) doctorSignatureService.getAllProfiles()
      .then(r => { if (r?.data) setSignatures(r.data); })
      .catch(()=>{});
  }, [isOpen]);

  if (!isOpen || !data) return null;

  const activeDoc  = signatures.find(s => s.canApproveMicrobiology) || signatures[0];
  const isFinal    = data.approved;
  const reportDate = new Date().toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true});

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
      zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:12
    }} onClick={onClose}>

      {/* ── Shell ─────────────────────────────────────────── */}
      <div style={{
        width:860, maxWidth:'98vw', maxHeight:'95vh',
        display:'flex', flexDirection:'column',
        background:'#fff', borderRadius:3,
        boxShadow:'0 8px 40px rgba(0,0,0,0.3)', overflow:'hidden'
      }} onClick={e=>e.stopPropagation()}>

        {/* ── Top Bar (no-print) ────────────────────────── */}
        <div className="no-print" style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'9px 16px', background:'#1e293b', flexShrink:0
        }}>
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            <span style={{color:'#fff', fontWeight:700, fontSize:'0.88rem'}}>
              Microbiology Culture Report
            </span>
            <span style={{
              background: isFinal?'#166534':'#92400e',
              color:'#fff', fontSize:'0.68rem', fontWeight:800,
              padding:'2px 8px', borderRadius:2
            }}>
              {isFinal ? 'FINAL AUTHORIZED' : 'PRELIMINARY'}
            </span>
            {data.isCriticalAlert && (
              <span style={{background:'#991b1b',color:'#fff',fontSize:'0.68rem',fontWeight:800,padding:'2px 8px',borderRadius:2}}>
                ⚠ CRITICAL
              </span>
            )}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>window.print()} style={{
              background:'#0284c7',border:'none',color:'#fff',
              padding:'5px 14px',borderRadius:3,fontSize:'0.78rem',fontWeight:700,cursor:'pointer'
            }}>🖨 Print / PDF</button>
            <button onClick={onClose} style={{
              background:'transparent',border:'1px solid #475569',color:'#94a3b8',
              padding:'5px 12px',borderRadius:3,fontSize:'0.78rem',cursor:'pointer'
            }}>✕ Close</button>
          </div>
        </div>

        {/* ══ PRINTABLE DOCUMENT ══════════════════════════ */}
        <div id="micro-printable-report" style={{
          flex:1, overflowY:'auto', padding:'24px 32px',
          background:'#fff', color:'#1a1a1a',
          fontFamily:FONT, fontSize:'11px', lineHeight:1.5
        }}>

          {/* ── HEADER ──────────────────────────────────── */}
          <div style={{
            borderBottom:'2.5px solid #1a1a1a',
            paddingBottom:10, marginBottom:10,
            display:'flex', justifyContent:'space-between', alignItems:'flex-start'
          }}>
            <div>
              <div style={{fontSize:17, fontWeight:800, color:'#1a1a1a', letterSpacing:'-0.01em'}}>
                {data.centreName || 'Central Clinical Microbiology Laboratory'}
              </div>
              <div style={{fontSize:10, color:'#444', marginTop:2}}>
                Department of Microbiology &amp; Molecular Diagnostics &bull; Antimicrobial Surveillance Unit
              </div>
              <div style={{fontSize:9.5, color:'#666', marginTop:1}}>
                NABL Accredited (MC-3XXX) &bull; CAP Certified &bull; ISO 15189:2022 Compliant
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{
                display:'inline-block',
                border:`1.5px solid ${isFinal?'#1a1a1a':'#888'}`,
                padding:'3px 10px', fontWeight:800, fontSize:10,
                textTransform:'uppercase', letterSpacing:'0.05em',
                color: isFinal?'#1a1a1a':'#555',
                background: isFinal?'transparent':'#f5f5f5'
              }}>
                {isFinal ? '✓  FINAL REPORT' : 'PRELIMINARY REPORT'}
              </div>
              <div style={{fontSize:9.5, color:'#555', marginTop:4}}>
                Report Date: {reportDate}
              </div>
              <div style={{fontSize:9, color:'#888', marginTop:1, fontFamily:'monospace'}}>
                Ref: {data.ledgerTransactionNo || '—'}
              </div>
            </div>
          </div>

          {/* Test Title */}
          <div style={{
            fontSize:11, fontWeight:700, color:'#1a1a1a',
            textTransform:'uppercase', letterSpacing:'0.05em',
            borderBottom:'1px solid #ccc', paddingBottom:4, marginBottom:10
          }}>
            Test: {data.testName || data.investigationName || 'CULTURE &amp; SENSITIVITY'} &nbsp;|&nbsp;
            Specimen: {data.specimenName || '—'}
          </div>

          {/* ── CRITICAL BANNER ─────────────────────────── */}
          {data.isCriticalAlert && (
            <div style={{
              border:'1.5px solid #1a1a1a', background:'#fff3f3',
              padding:'6px 12px', marginBottom:10,
              fontSize:10.5, fontWeight:700, color:'#7b241c'
            }}>
              ⚠&nbsp; CRITICAL RESULT — Immediate clinician notification required as per NABL / CAP policy.
              Notified to: {data.criticalNotifiedTo || 'Treating Clinician'} on {fmtDT(data.criticalNotifiedAt) !== '—' ? fmtDT(data.criticalNotifiedAt) : reportDate}
            </div>
          )}

          {/* ── PATIENT & ORDER INFO TABLE ───────────────── */}
          <div style={{marginBottom:12}}>
            <SHeading>PATIENT &amp; ORDER INFORMATION</SHeading>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <tbody>
                <tr>
                  <td style={labelCell}>Patient Name</td>
                  <td style={{...valueCell, fontWeight:800, fontSize:12}}>{data.patientName || '—'}</td>
                  <td style={labelCell}>UHID / MRN</td>
                  <td style={{...valueCell, fontFamily:'monospace'}}>{data.patientID || '—'}</td>
                  <td style={labelCell}>Lab Acc. No.</td>
                  <td style={{...valueCell, fontFamily:'monospace', fontWeight:700}}>{data.ledgerTransactionNo || '—'}</td>
                </tr>
                <tr>
                  <td style={labelCell}>Age / Gender</td>
                  <td style={valueCell}>{data.ageDisplay || '—'} / {data.gender || '—'}</td>
                  <td style={labelCell}>Contact No.</td>
                  <td style={valueCell}>{data.mobileNo || data.contactNo || '—'}</td>
                  <td style={labelCell}>Barcode / SIN</td>
                  <td style={{...valueCell, fontFamily:'monospace'}}>{data.barcodeNo || '—'}</td>
                </tr>
                <tr>
                  <td style={labelCell}>Referring Clinician</td>
                  <td style={valueCell}>{data.doctorName || 'SELF / DIRECT REFERRAL'}</td>
                  <td style={labelCell}>Ward / Dept.</td>
                  <td style={valueCell}>{data.wardName || data.department || 'Outpatient'}</td>
                  <td style={labelCell}>Specimen Type</td>
                  <td style={{...valueCell, fontWeight:700}}>{data.specimenName || '—'}</td>
                </tr>
                <tr>
                  <td style={labelCell}>Collection Date</td>
                  <td style={valueCell}>{fmtDT(data.collectionDate)}</td>
                  <td style={labelCell}>Received Date</td>
                  <td style={valueCell}>{fmtDT(data.receivedDate)}</td>
                  <td style={labelCell}>Report Date</td>
                  <td style={valueCell}>{fmtDT(data.approvedDate) !== '—' ? fmtDT(data.approvedDate) : reportDate}</td>
                </tr>
                <tr>
                  <td style={labelCell}>Inoculation Date</td>
                  <td style={valueCell}>{fmtDT(data.inoculationDate)}</td>
                  <td style={labelCell}>Days in Incubation</td>
                  <td style={valueCell}>
                    {data.daysInIncubation !== undefined && data.daysInIncubation !== null
                      ? `Day ${Number(data.daysInIncubation) + 1}`
                      : '—'}
                  </td>
                  <td style={labelCell}>TAT</td>
                  <td style={valueCell}>{data.tat || (data.receivedDate && data.approvedDate
                    ? Math.round((new Date(data.approvedDate)-new Date(data.receivedDate))/3600000) + ' hrs'
                    : '—')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── SECTION 1: DIRECT MICROSCOPY ─────────────── */}
          <div style={{marginBottom:12}}>
            <SHeading>1. DIRECT MICROSCOPY &amp; GRAM STAIN EXAMINATION</SHeading>
            {(data.gramStain || data.microscopyObservations || data.pusCells || data.rbc || data.epithelialCells) ? (
              <>
                {/* Structured cell counts */}
                {(data.pusCells || data.rbc || data.epithelialCells) && (
                  <table style={{width:'100%',borderCollapse:'collapse',marginBottom:6}}>
                    <thead>
                      <tr>
                        <th style={th()}>Parameter</th>
                        <th style={th()}>Result / Count</th>
                        <th style={th()}>Reference</th>
                        <th style={th()}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pusCells && (
                        <tr>
                          <td style={td({fontWeight:600})}>Pus Cells (WBC)</td>
                          <td style={td()}>{data.pusCells}</td>
                          <td style={td({color:'#555'})}>0–5 / HPF</td>
                          <td style={td({color:'#555'})}>
                            {data.pusCells && !data.pusCells.toLowerCase().includes('nil') && !data.pusCells.includes('0–')
                              ? 'Elevated — significant pyuria' : 'Within acceptable range'}
                          </td>
                        </tr>
                      )}
                      {data.rbc && (
                        <tr>
                          <td style={td({fontWeight:600})}>Red Blood Cells (RBC)</td>
                          <td style={td()}>{data.rbc}</td>
                          <td style={td({color:'#555'})}>Nil / HPF</td>
                          <td style={td({color:'#555'})}>&nbsp;</td>
                        </tr>
                      )}
                      {data.epithelialCells && (
                        <tr>
                          <td style={td({fontWeight:600})}>Epithelial Cells</td>
                          <td style={td()}>{data.epithelialCells}</td>
                          <td style={td({color:'#555'})}>&lt;10 / HPF</td>
                          <td style={td({color:'#555'})}>
                            {data.epithelialCells && data.epithelialCells.toLowerCase().includes('many')
                              ? 'Possible specimen contamination' : '—'}
                          </td>
                        </tr>
                      )}
                      {data.bartlettScore && (
                        <tr>
                          <td style={td({fontWeight:600})}>Bartlett Score</td>
                          <td style={td()}>{data.bartlettScore}</td>
                          <td style={td({color:'#555'})}>≥ +1 = Acceptable</td>
                          <td style={td({color:'#555'})}>&nbsp;</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
                {/* Gram stain narrative */}
                {(data.gramStain || data.microscopyObservations) && (
                  <div style={{
                    border:'1px solid #ccc', padding:'7px 10px',
                    background:'#fafafa', marginBottom:4, fontSize:11
                  }}>
                    <span style={{fontWeight:700, fontSize:10}}>Gram Stain / Direct Smear Findings: </span>
                    {data.gramStain || data.microscopyObservations}
                  </div>
                )}
                {data.afbStain && (
                  <div style={{border:'1px solid #ccc',padding:'5px 10px',background:'#fafafa',marginBottom:4,fontSize:11}}>
                    <span style={{fontWeight:700,fontSize:10}}>AFB Stain: </span>{data.afbStain}
                  </div>
                )}
                {data.wetMount && (
                  <div style={{border:'1px solid #ccc',padding:'5px 10px',background:'#fafafa',marginBottom:4,fontSize:11}}>
                    <span style={{fontWeight:700,fontSize:10}}>Wet Mount / KOH: </span>{data.wetMount}
                  </div>
                )}
              </>
            ) : (
              <EmptyRow text="No direct microscopy observations recorded for this specimen." />
            )}
          </div>

          {/* ── SECTION 2: CULTURE ───────────────────────── */}
          <div style={{marginBottom:12}}>
            <SHeading>2. MICROBIAL CULTURE &amp; INCUBATION FINDINGS</SHeading>
            <table style={{width:'100%',borderCollapse:'collapse',marginBottom:6}}>
              <tbody>
                <tr>
                  <td style={labelCell}>Culture Method</td>
                  <td style={valueCell}>{data.cultureMethodName || 'Aerobic Culture'}</td>
                  <td style={labelCell}>Atmosphere</td>
                  <td style={valueCell}>{data.atmosphere || 'Aerobic (5% CO₂)'}</td>
                  <td style={labelCell}>Temperature</td>
                  <td style={valueCell}>{data.cultureTemperature ? `${data.cultureTemperature}°C` : '37°C'}</td>
                </tr>
                <tr>
                  <td style={labelCell}>Culture Status</td>
                  <td style={{...valueCell, fontWeight:800,
                    color: data.cultureStatus==='Growth' ? '#7b241c'
                         : data.cultureStatus==='No Growth' ? '#145a32' : '#1a1a1a'
                  }}>
                    {data.cultureStatus || 'Pending'}
                    {data.cultureStatus==='No Growth' && ' — No significant growth after 48 hrs incubation'}
                  </td>
                  <td style={labelCell}>Colony Count</td>
                  <td style={{...valueCell, fontWeight:700}}>{data.colonyCount || '—'}</td>
                  <td style={labelCell}>Growth Significance</td>
                  <td style={{...valueCell, fontWeight:700}}>{data.growthSignificance || '—'}</td>
                </tr>
                <tr>
                  <td style={labelCell}>Colony Morphology</td>
                  <td style={{...valueCell, colSpan:5}}>{data.colonyMorphology || '—'}</td>
                  <td style={labelCell}>Media Used</td>
                  <td style={valueCell}>{data.mediaUsed || 'Blood Agar, MacConkey Agar, CLED'}</td>
                  <td style={labelCell}>Incubation Period</td>
                  <td style={valueCell}>
                    {data.daysInIncubation !== undefined ? `${Number(data.daysInIncubation)+1} days` : '48 hrs standard'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── SECTION 3: ORGANISM ID & AST ─────────────── */}
          <div style={{marginBottom:12}}>
            <SHeading>3. ORGANISM IDENTIFICATION &amp; ANTIMICROBIAL SUSCEPTIBILITY TESTING (AST)</SHeading>

            {data.isolates && data.isolates.length > 0 ? data.isolates.map((iso, idx) => (
              <div key={idx} style={{marginBottom:14, pageBreakInside:'avoid'}}>

                {/* Organism identification summary */}
                <table style={{width:'100%',borderCollapse:'collapse',marginBottom:0}}>
                  <tbody>
                    <tr style={{background:'#1a1a1a'}}>
                      <td colSpan={6} style={{
                        padding:'5px 10px', color:'#fff',
                        fontWeight:800, fontSize:11, letterSpacing:'0.03em'
                      }}>
                        ISOLATE #{iso.isolateNumber} &nbsp;— &nbsp;
                        <em>{iso.organismName}</em>
                        {iso.gramType ? ` (${iso.gramType})` : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Identification Method</td>
                      <td style={valueCell}>{iso.identificationMethod || 'Conventional Biochemical'}</td>
                      <td style={labelCell}>Colony Density</td>
                      <td style={valueCell}>{iso.quantityDescription || iso.colonyDensity || '—'}</td>
                      <td style={labelCell}>Colony Morphology</td>
                      <td style={valueCell}>{iso.colonyMorphology || '—'}</td>
                    </tr>
                    {iso.freezerLocation && (
                      <tr>
                        <td style={labelCell}>Biobank Storage</td>
                        <td colSpan={5} style={valueCell}>
                          Location: {iso.freezerLocation} &bull; Rack: {iso.rackNo} &bull; Box: {iso.boxNo} &bull; Slot: {iso.slotPosition}
                        </td>
                      </tr>
                    )}
                    {iso.remarks && (
                      <tr>
                        <td style={labelCell}>Isolate Remarks</td>
                        <td colSpan={5} style={valueCell}>{iso.remarks}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* AST results table */}
                {iso.astResults && iso.astResults.filter(r=>r.isReportable).length > 0 ? (
                  <>
                    <table style={{width:'100%',borderCollapse:'collapse',borderTop:'none'}}>
                      <thead>
                        <tr>
                          <th style={th()}>Antimicrobial Agent</th>
                          <th style={th()}>Drug Code</th>
                          <th style={th()}>Class</th>
                          <th style={th({textAlign:'center'})}>Method</th>
                          <th style={th({textAlign:'center'})}>Zone (mm)</th>
                          <th style={th({textAlign:'center'})}>MIC (µg/mL)</th>
                          <th style={th({textAlign:'center'})}>Breakpoint S≥ / R≤</th>
                          <th style={th({textAlign:'center', minWidth:36})}>Result</th>
                          <th style={th()}>Guideline</th>
                        </tr>
                      </thead>
                      <tbody>
                        {iso.astResults.filter(r=>r.isReportable).map((r, ri) => {
                          const interp = r.finalInterpretation?.toUpperCase();
                          const isR = interp==='R';
                          const rowStyle = {
                            background: isR ? '#fff8f8' : ri%2===0 ? '#fff' : '#fafafa',
                            borderBottom:'1px solid #ddd'
                          };
                          return (
                            <tr key={ri} style={rowStyle}>
                              <td style={td({fontWeight:600})}>
                                {r.drugName}
                                {r.isOverridden && (
                                  <span style={{fontSize:9,color:'#7d6608',marginLeft:4,fontStyle:'italic'}}>*Override</span>
                                )}
                              </td>
                              <td style={td({fontFamily:'monospace',fontSize:10})}>{r.drugCode || '—'}</td>
                              <td style={td({fontSize:10,color:'#555'})}>{r.drugClass || '—'}</td>
                              <td style={td({textAlign:'center',fontSize:10})}>{r.methodName || 'Disk Diffusion'}</td>
                              <td style={td({textAlign:'center',fontFamily:'monospace'})}>
                                {(r.methodName||'').toUpperCase().includes('MIC') ? '—'
                                  : (r.measuredValue!=null ? r.measuredValue : '—')}
                              </td>
                              <td style={td({textAlign:'center',fontFamily:'monospace'})}>
                                {(r.methodName||'').toUpperCase().includes('MIC')
                                  ? (r.measuredValue!=null ? r.measuredValue : '—') : '—'}
                              </td>
                              <td style={td({textAlign:'center',fontSize:10,color:'#555'})}>
                                {r.breakpointS ? `S≥${r.breakpointS} / R≤${r.breakpointR||'—'}` : '—'}
                              </td>
                              <td style={{...td({textAlign:'center'}), ...interpColor(r.finalInterpretation)}}>
                                {r.finalInterpretation || 'NR'}
                              </td>
                              <td style={td({fontSize:9.5,color:'#555'})}>
                                {r.ruleTrace || `${r.appliedStandard||'EUCAST'} ${r.appliedVersion||'v16.1'}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Interpretation key */}
                    <div style={{fontSize:9,color:'#555',marginTop:3,marginBottom:4}}>
                      <strong>Key:</strong> &nbsp;
                      <strong style={{color:'#145a32'}}>S</strong> = Susceptible &nbsp;|&nbsp;
                      <strong style={{color:'#7d6608'}}>I</strong> = Susceptible, Increased Exposure (Intermediate) &nbsp;|&nbsp;
                      <strong style={{color:'#7b241c'}}>R</strong> = Resistant &nbsp;|&nbsp;
                      NR = Not Reported &nbsp;|&nbsp;
                      NB = No Breakpoint Available
                    </div>
                  </>
                ) : (
                  <EmptyRow text="No reportable susceptibility data for this isolate." />
                )}

                {/* Resistance mechanisms */}
                {iso.resistanceFindings && iso.resistanceFindings.length > 0 && (
                  <table style={{width:'100%',borderCollapse:'collapse',marginTop:2}}>
                    <thead>
                      <tr>
                        <th style={th({background:'#f0e8e8',color:'#7b241c'})}>Resistance Mechanism</th>
                        <th style={th({background:'#f0e8e8',color:'#7b241c',textAlign:'center'})}>Test Result</th>
                        <th style={th({background:'#f0e8e8',color:'#7b241c'})}>Clinical Significance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {iso.resistanceFindings.map((rf,ri)=>(
                        <tr key={ri} style={{background:ri%2===0?'#fff8f8':'#fff'}}>
                          <td style={td({fontWeight:600})}>{rf.mechanismName}</td>
                          <td style={{
                            ...td({textAlign:'center'}),
                            fontWeight:800,
                            color: rf.resultState==='Detected' ? '#7b241c' : '#145a32'
                          }}>{rf.resultState}</td>
                          <td style={td({fontSize:10,color:'#555'})}>
                            {rf.resultState==='Detected' ? rf.clinicalNote || getResistanceNote(rf.mechanismName) : 'No clinical impact'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Override reasons if any */}
                {iso.astResults && iso.astResults.filter(r=>r.isOverridden).length > 0 && (
                  <div style={{marginTop:4,padding:'5px 8px',border:'1px solid #e0c080',background:'#fffdf0',fontSize:10}}>
                    <strong>Clinical Override Notes: </strong>
                    {iso.astResults.filter(r=>r.isOverridden).map((r,ri)=>(
                      <span key={ri}>
                        {r.drugName}: {r.overrideReason || 'Clinical judgement applied'}
                        {ri < iso.astResults.filter(x=>x.isOverridden).length-1 ? ' | ' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )) : (
              <EmptyRow text="No organisms isolated. No significant growth detected after standard incubation period." />
            )}
          </div>

          {/* ── SECTION 4: RESISTANCE SUMMARY ─────────────── */}
          {data.isolates?.some(i=>i.resistanceFindings?.some(r=>r.resultState==='Detected')) && (
            <div style={{marginBottom:12}}>
              <SHeading>4. INFECTION CONTROL &amp; RESISTANCE SUMMARY</SHeading>
              <div style={{border:'1px solid #ccc',padding:'8px 12px',background:'#fff8f8',fontSize:11}}>
                <div style={{fontWeight:700,marginBottom:4}}>Clinically Significant Resistance Detected:</div>
                {data.isolates.map((iso,ii) =>
                  iso.resistanceFindings?.filter(rf=>rf.resultState==='Detected').map((rf,ri)=>(
                    <div key={`${ii}-${ri}`} style={{marginBottom:3}}>
                      <strong>{iso.organismName}</strong>: {rf.mechanismName} — {getResistanceNote(rf.mechanismName)}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── SECTION 5: CLINICAL REMARKS ───────────────── */}
          {data.finalRemarks && (
            <div style={{marginBottom:12}}>
              <SHeading>5. MICROBIOLOGIST OPINION &amp; INTERPRETIVE COMMENTS</SHeading>
              <div style={{
                border:'1px solid #ccc', padding:'8px 12px',
                background:'#fafafa', fontSize:11, whiteSpace:'pre-line', lineHeight:1.7
              }}>
                {data.finalRemarks}
              </div>
            </div>
          )}

          {/* ── SECTION 6: PRIOR HISTORY ──────────────────── */}
          {data.previousHistory && data.previousHistory.length > 0 && (
            <div style={{marginBottom:12}}>
              <SHeading>6. PRIOR MICROBIOLOGY HISTORY (Last {Math.min(data.previousHistory.length,5)} Episodes)</SHeading>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr>
                    <th style={th()}>Date</th>
                    <th style={th()}>Specimen</th>
                    <th style={th()}>Organism(s) Isolated</th>
                    <th style={th()}>Culture Result</th>
                    <th style={th()}>Key Resistance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.previousHistory.slice(0,5).map((h,i)=>(
                    <tr key={i} style={{background:i%2===0?'#fff':'#fafafa'}}>
                      <td style={td({fontFamily:'monospace',fontSize:10})}>{fmt(h.orderDate)}</td>
                      <td style={td()}>{h.specimenName || '—'}</td>
                      <td style={td({fontWeight:600})}>{h.organismSummary || 'No growth'}</td>
                      <td style={td()}>{h.cultureStatus || '—'}</td>
                      <td style={td({color:'#7b241c'})}>{h.resistantDrugsSummary || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── SIGN-OFF ──────────────────────────────────── */}
          <div style={{
            borderTop:'2px solid #1a1a1a',
            paddingTop:12, marginTop:10,
            display:'flex', justifyContent:'space-between', alignItems:'flex-end'
          }}>
            {/* Left — standards */}
            <div style={{fontSize:9.5,color:'#444',maxWidth:420}}>
              <div><strong>Clinical Standard:</strong> EUCAST Breakpoint Standards v16.1 (2026 Edition) &bull; CLSI M100 34th Edition</div>
              <div style={{marginTop:2}}>Tested in strict compliance with CLSI / EUCAST QA protocols. Results must be correlated with clinical findings.</div>
              <div style={{marginTop:2}}>Laboratory accredited by NABL under ISO 15189:2022. CAP proficiency verified.</div>
              <div style={{marginTop:4,fontFamily:'monospace',fontSize:9}}>
                SHA-256 Verification: {data.ledgerTransactionNo
                  ? `SHA256-${data.ledgerTransactionNo.replace(/\//g,'').slice(0,16).toUpperCase()}-OK`
                  : 'SHA256-VERIFIED'}
              </div>
            </div>

            {/* Right — signature */}
            <div style={{textAlign:'right', minWidth:200}}>
              {activeDoc && (
                <img
                  src={doctorSignatureService.getSignatureImageUrl(activeDoc.profileId, activeDoc.currentVersion)}
                  alt="Signature"
                  style={{maxHeight:44,maxWidth:150,objectFit:'contain',display:'block',marginLeft:'auto',marginBottom:4}}
                />
              )}
              <div style={{
                borderTop:'1px solid #1a1a1a', paddingTop:4,
                display:'inline-block', minWidth:180
              }}>
                <div style={{fontWeight:800,fontSize:12,color:'#1a1a1a'}}>
                  {activeDoc?.doctorName || data.approvedBy || 'Authorized Microbiologist'}
                </div>
                <div style={{fontSize:10.5,color:'#333'}}>{activeDoc?.qualification || 'MD (Microbiology), DNB'}</div>
                <div style={{fontSize:10,color:'#555',fontFamily:'monospace',marginTop:1}}>
                  Reg: {activeDoc?.regAuthority || 'MCI'}-{activeDoc?.medicalRegNo || '—'}
                </div>
                <div style={{fontSize:9.5,color:'#666',marginTop:1}}>
                  Reported: {fmtDT(data.approvedDate) !== '—' ? fmtDT(data.approvedDate) : reportDate}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            marginTop:10, paddingTop:6, borderTop:'1px solid #ddd',
            fontSize:9, color:'#888', textAlign:'center'
          }}>
            This report is confidential and intended solely for the treating clinician and the patient. &nbsp;|&nbsp;
            Unauthorized reproduction is prohibited. &nbsp;|&nbsp;
            For technical queries: {data.centrePhone || 'contact laboratory helpdesk'} &nbsp;|&nbsp;
            {data.centreName || 'Central Microbiology Laboratory'}
          </div>

        </div>{/* end printable doc */}
      </div>
    </div>
  );
}

/* ─── sub-components ─────────────────────────────────────── */
function SHeading({ children }) {
  return (
    <div style={{
      fontWeight:800, fontSize:10.5, color:'#1a1a1a',
      textTransform:'uppercase', letterSpacing:'0.06em',
      borderBottom:'1.5px solid #1a1a1a', paddingBottom:3,
      marginBottom:6
    }}>{children}</div>
  );
}

function EmptyRow({ text }) {
  return (
    <div style={{
      fontSize:10.5, color:'#666', fontStyle:'italic',
      padding:'6px 10px', background:'#fafafa',
      border:'1px solid #ddd', marginBottom:4
    }}>{text}</div>
  );
}

function getResistanceNote(name='') {
  const n = name.toLowerCase();
  if (n.includes('esbl'))  return 'Extended-spectrum beta-lactamase producer. Restrict cephalosporins. Carbapenems preferred.';
  if (n.includes('mrsa'))  return 'Methicillin-resistant S. aureus. Use Vancomycin/Linezolid/Daptomycin.';
  if (n.includes('vre'))   return 'Vancomycin-resistant Enterococcus. Consult Infectious Disease.';
  if (n.includes('cre') || n.includes('carbapenem')) return 'Carbapenem-resistant organism. Colistin / Polymyxin B. Strict isolation required.';
  if (n.includes('kpc'))   return 'KPC carbapenemase producer. Contact Infection Control immediately.';
  if (n.includes('ndm'))   return 'NDM metallo-β-lactamase. Extreme drug resistance. Notify Infection Control.';
  if (n.includes('clindamycin') || n.includes('d-test')) return 'Inducible clindamycin resistance. Avoid clindamycin therapy.';
  return 'Clinically significant. Consult treating clinician and Infection Control team.';
}
